import React, { useCallback, useState } from 'react';
import { asDraggableContentListItem, useSortableListItem } from '../../../_lib/components/SortableContentList';
import { ContainerWithCommonContextMenu } from '../../../_lib/components/ContextMenu';
import { ContentLink } from '../../../_lib/components/ContentLink';
import { HyperlinkTarget } from '../../../components/atoms/Hyperlink';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { StatusInfoError } from '../../../_lib/components/StatusInfo';
import ILink from '../../../types/ILink';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { ContextMenuButtonProps } from '../../../_lib/components/ContextMenu/partials/ContextMenuButton';
import { SonForm, TextParam, UrlParam } from '../../../_lib/Form2';
import DeletableItemForm from '../../../components/forms/DeletableItem/DeletableItemForm';
import { sendLiveRegionMessage } from '../../../components/atoms/LiveRegionGlobal/LiveRegionGlobal';
import IForm from '../../../types/IForm';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { AddExternalFavoriteForm } from './AddExternalFavoriteForm';
import { useRefreshView } from '../../../_lib/ViewFetcher';
import { useEditButtonRefContext } from './EditButtonRefContext';
import { StatusInfoModifier } from '../../../_lib/components/StatusInfo/partials/StatusInfo';

type FavoriteListItemProps = {
  id: string;
  link: ILink;
  removeForm: IForm & { onSuccess?: () => void };
  editForm: SonForm<TextParam<'title'> | UrlParam<'link'>>;
};

function FavouriteListItem({ id, link, removeForm, editForm }: FavoriteListItemProps) {
  const { t } = useSonTranslation('FavoritedPages');
  const { refreshView } = useRefreshView();
  const editButtonRef = useEditButtonRefContext();

  const [view, setView] = useState<'remove' | 'edit' | undefined>(undefined);

  const { move, nextItemId, prevItemId, error } = useSortableListItem(id);

  const moveElementUp = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (prevItemId) {
        event.preventDefault();
        event.stopPropagation();

        move(prevItemId);
      }
    },
    [move, prevItemId]
  );

  const moveElementDown = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (nextItemId) {
        event.preventDefault();
        event.stopPropagation();

        move(nextItemId);
      }
    },
    [move, nextItemId]
  );

  const contextMenuActions: ContextMenuButtonProps[] = [
    {
      children: t('editForm.prompt'),
      key: t('editForm.prompt'),
      name: 'editForm',
      'aria-haspopup': true,
      onClick: event => {
        event.preventDefault();
        event.stopPropagation();

        setView('edit');
      },
    },
    {
      children: t('removeForm.prompt'),
      key: t('removeForm.prompt'),
      name: 'removeForm',
      onClick: event => {
        event.preventDefault();
        event.stopPropagation();

        setView('remove');
      },
    },
    {
      children: t('moveElementUp'),
      key: 'moveElementUp',
      disabled: !prevItemId,
      onClick: moveElementUp,
    },
    {
      children: t('moveElementDown'),
      key: 'moveElementDown',
      disabled: !nextItemId,
      onClick: moveElementDown,
    },
  ];

  if (view === 'remove') {
    return removeForm ? (
      <DeletableItemForm
        id="favorit-delete-form"
        ariaLabel={t('deletableItem.removeQuestion')}
        cancelAction={() => setView(undefined)}
        model={removeForm}
        onSuccess={() => {
          if (nextItemId) {
            document.getElementById(nextItemId)?.getElementsByTagName('button')[0].focus();
          } else if (prevItemId) {
            document.getElementById(prevItemId)?.getElementsByTagName('button')[0].focus();
          } else if (editButtonRef?.current) {
            editButtonRef.current.focus();
          }

          sendLiveRegionMessage(t('removeForm.success'));

          if (typeof removeForm.onSuccess === 'function') {
            removeForm.onSuccess();
          }

          refreshView();
        }}
      />
    ) : null;
  }

  return (
    <>
      <ContainerWithCommonContextMenu
        toolTip={t('manageMenuToolTip', { title: link.name })}
        actions={contextMenuActions}
      >
        <ContentLink
          to={link.url}
          external={link.external}
          target={link.external ? HyperlinkTarget.BLANK : undefined}
          title={t('phrases:linkOpensInNewWindow')}
          tooltipPlacement={TooltipPlacement.BOTTOM}
        >
          {link.name || link.url}
        </ContentLink>
      </ContainerWithCommonContextMenu>
      {error && <StatusInfoError message={t('sortErrorResponse')} modifier={StatusInfoModifier.PADDING_TOP} />}
      {view === 'edit' ? (
        <ModalDialog
          initialFocusIndex={1}
          closeModal={() => setView(undefined)}
          headline={t('editForm.prompt', { title: link.name })}
        >
          <AddExternalFavoriteForm form={editForm} closeModal={() => setView(undefined)} />
        </ModalDialog>
      ) : null}
    </>
  );
}

export const DraggableFavouriteListItem = asDraggableContentListItem(FavouriteListItem);
